import { CircularProgress } from '@material-ui/core';
import Collapse from '@material-ui/core/Collapse';
import { alpha, styled, withStyles, withTheme } from '@material-ui/core/styles';
import SvgIcon from '@material-ui/core/SvgIcon';
import TreeItem from '@material-ui/lab/TreeItem';
import TreeView from '@material-ui/lab/TreeView';
import { Icons } from '@nutrien/cxp-components';
import { observer } from 'mobx-react-lite';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

import { useMst } from '../../mobx-models/Root';
import type {
  AssetsByAreaHeirachyDocument,
  HeirachyResult,
} from '../../rxdb/assetsByAreaHeirachy/assetsByAreaHierarchy.types';
import { MobileComponentLocationHeirarchy } from '../../rxdb/inspectionList/queryBuilder';
import { Discipline, Site } from '../../utilities/enums';
import useStyles from './AssetLocationTreeView.styles';

function TransitionComponent(props: object) {
  return (
    <div>
      <Collapse {...props} />
    </div>
  );
}

TransitionComponent.propTypes = {
  in: PropTypes.bool,
};

const StyledTreeItem = withStyles(theme => ({
  iconContainer: {
    '& .close': {
      opacity: 0.3,
    },
  },
  root: {
    '&.MuiTreeItem-root> .MuiTreeItem-content': {
      padding: '8px 0px 8px 19px',
      alignItems: 'baseline',
    },
    '&.MuiTreeItem-root.Mui-selected > .MuiTreeItem-content ': {
      backgroundColor: 'rgba(42, 171, 63, 0.12)',
      border: '1px solid #2AAB3F',
      borderRadius: '4px',
    },
    '&.MuiTreeItem-root.Mui-selected > .MuiTreeItem-content .MuiTreeItem-label': {
      backgroundColor: 'transparent',
    },

    '&.MuiTreeItem-root > .MuiTreeItem-content:hover': {
      background:
        'linear-gradient(0deg, rgba(255, 255, 255, 0.24), rgba(255, 255, 255, 0.24)), #2AAB3F;',
      borderRadius: '4px',
      '&.MuiTreeItem-root > .MuiTreeItem-content > .makeStyles-addIcon-40': {
        color: 'black',
      },
    },
    '&.MuiTreeItem-root > .MuiTreeItem-content:hover .MuiTreeItem-label': {
      backgroundColor: 'transparent',
    },
    '@media (hover: none)': {},
  },
  group: {
    marginLeft: 24,
    borderLeft: `1px dashed ${alpha(theme.palette.text.primary, 0.4)}`,
  },
}))(props => <TreeItem nodeId="" {...props} TransitionComponent={TransitionComponent} />);

interface CustomTreeViewProps {
  type: string;
  nodeId: string;
  label: React.ReactNode;
  id: string;
  onClick: (e: React.MouseEvent<HTMLElement>) => void;
}

const CustomTreeView = withTheme(
  styled((props: CustomTreeViewProps) => <StyledTreeItem {...props} />)({}),
);

interface Props {
  assetButton: string;
  assetLocationName: string;
  onChange: (val: number) => void;
  assetName: string;
  assetsByAreaHierarchyData: AssetsByAreaHeirachyDocument[];
  areaListFromRxDB: MobileComponentLocationHeirarchy[];
}

const RenderTreeView = ({
  assetButton,
  onChange,
  assetName,
  assetsByAreaHierarchyData,
  areaListFromRxDB,
}: Props) => {
  const [selected, setSelected] = useState<string>();
  const classes = useStyles();
  const { SiteDiscipline } = useMst();
  const isRocanville = SiteDiscipline.siteId === Site.ROCANVILLE;
  const isShaft = SiteDiscipline.disciplineId === Discipline.Shaft;
  const isElectrical = SiteDiscipline.disciplineId === Discipline.Electrical;
  const subAreaLevel = 2;

  useEffect(() => {
    setTimeout(() => {
      const defaultScroll: HTMLElement = document.getElementById(assetButton) as HTMLElement;
      if (defaultScroll && defaultScroll.scrollIntoView) defaultScroll.scrollIntoView();
    }, 100);
  }, []);

  const onTreeItemClick = (node: string, e: React.MouseEvent<HTMLElement>, locationID: number) => {
    setSelected(node);
    onChange(locationID);
    e.stopPropagation();
  };

  useEffect(() => {
    setSelected(assetName);
    onChange(Number(assetButton));
  }, [assetButton]);

  const filteredSurface = {
    id: 1,
    type: '1',
    name: 'Surface',
    children: assetsByAreaHierarchyData?.length
      ? assetsByAreaHierarchyData[0]?.children
      : ([] as unknown as [HeirachyResult]),
  } as HeirachyResult;

  const filteredUnderground = {
    id: 2,
    type: '2',
    name: 'Underground',
    children: assetsByAreaHierarchyData?.length
      ? assetsByAreaHierarchyData[1]?.children
      : ([] as unknown as [HeirachyResult]),
  } as HeirachyResult;

  const filteredShaft = {
    id: 3,
    type: '3',
    name: 'Shaft',
    children: assetsByAreaHierarchyData?.length
      ? assetsByAreaHierarchyData[2]?.children
      : ([] as unknown as [HeirachyResult]),
  } as HeirachyResult;

  const findParent = (
    arr: AssetsByAreaHeirachyDocument[] | HeirachyResult[],
    id: string,
    currentLevel = 0,
  ): boolean | string[] => {
    for (let i = 0; i < arr.length; i++) {
      if (isShaft || isElectrical ? arr[i].id === Number(id) : arr[i].name === id) {
        return [];
      } else if (arr[i].children && arr[i].children.length) {
        const t = findParent(arr[i].children, id, currentLevel + 1);
        if (t) {
          (t as string[]).push(
            isElectrical
              ? `${arr[i].id.toString()}`
              : isShaft
              ? `${arr[i].id.toString()}${currentLevel}`
              : arr[i].name,
          );
          return t;
        }
      }
    }
    return false;
  };

  const getNodeName = (node: HeirachyResult) => {
    if (node.lowerGridLine && node.upperGridLine) {
      return `(${node.upperGridLine}-${node.lowerGridLine}${isRocanville ? ' m' : "'"}) ${
        node.name
      }`;
    }
    return node.name;
  };

  const renderTree = (nodes: HeirachyResult, currentLevel = 0) => (
    <CustomTreeView
      type={nodes.type}
      id={nodes.id.toString()}
      className={classes.treeViewData}
      key={nodes.id.toString() + currentLevel}
      nodeId={
        isElectrical
          ? `${nodes.id.toString()}`
          : isShaft
          ? `${nodes.id.toString()}${currentLevel}`
          : nodes.name
      }
      label={
        <div>
          <div className={classes.nodesStylesParent}>
            <span className={classes.nodeStylesChildren}>
              {getNodeName(nodes)}{' '}
              {areaListFromRxDB?.find((item: { id: string }) => item.id === nodes.id.toString()) ? (
                <Icons.CheckCircleIcon className={classes.sideIcon} />
              ) : (
                ''
              )}
            </span>
            <Icons.ChevronRightFeather
              className={classes.addIcon}
              color={selected === nodes.name ? 'white' : 'primary'}
              strokeWidth="1.5px"
            />
          </div>
        </div>
      }
      onClick={(e: React.MouseEvent<HTMLElement>) =>
        onTreeItemClick(nodes.name, e, Number(nodes.id))
      }
      data-testid={nodes.id.toString()}
    >
      {nodes.children &&
        nodes.children.length > 0 &&
        nodes.children.map(element => {
          return renderTree(element, currentLevel + 1);
        })}
    </CustomTreeView>
  );

  const MinusSquare = () => {
    return (
      <SvgIcon fontSize="inherit" classes={{ root: classes.treeCheck }}>
        <path d="M22.047 22.074v0 0-20.147 0h-20.12v0 20.147 0h20.12zM22.047 24h-20.12q-.803 0-1.365-.562t-.562-1.365v-20.147q0-.776.562-1.351t1.365-.575h20.147q.776 0 1.351.575t.575 1.351v20.147q0 .803-.575 1.365t-1.378.562v0zM17.873 11.023h-11.826q-.375 0-.669.281t-.294.682v0q0 .401.294 .682t.669.281h11.826q.375 0 .669-.281t.294-.682v0q0-.401-.294-.682t-.669-.281z" />
      </SvgIcon>
    );
  };

  const PlusSquare = () => {
    return (
      <SvgIcon fontSize="inherit" classes={{ root: classes.treeCheck }}>
        <path d="M22.047 22.074v0 0-20.147 0h-20.12v0 20.147 0h20.12zM22.047 24h-20.12q-.803 0-1.365-.562t-.562-1.365v-20.147q0-.776.562-1.351t1.365-.575h20.147q.776 0 1.351.575t.575 1.351v20.147q0 .803-.575 1.365t-1.378.562v0zM17.873 12.977h-4.923v4.896q0 .401-.281.682t-.682.281v0q-.375 0-.669-.281t-.294-.682v-4.896h-4.923q-.401 0-.682-.294t-.281-.669v0q0-.401.281-.682t.682-.281h4.923v-4.896q0-.401.294-.682t.669-.281v0q.401 0 .682.281t.281.682v4.896h4.923q.401 0 .682.281t.281.682v0q0 .375-.281.669t-.682.294z" />
      </SvgIcon>
    );
  };

  const LastNodeSquare = () => {
    return (
      <SvgIcon className="close" fontSize="inherit" classes={{ root: classes.treeCheck }}>
        <path d="M22.047 22.074v0 0-20.147 0h-20.12v0 20.147 0h20.12zM22.047 24h-20.12q-.803 0-1.365-.562t-.562-1.365v-20.147q0-.776.562-1.351t1.365-.575h20.147q.776 0 1.351.575t.575 1.351v20.147q0 .803-.575 1.365t-1.378.562v0zM17.873 12.977h-4.923v4.896q0 .401-.281.682t-.682.281v0q-.375 0-.669-.281t-.294-.682v-4.896h-4.923q-.401 0-.682-.294t-.281-.669v0q0-.401.281-.682t.682-.281h4.923v-4.896q0-.401.294-.682t.669-.281v0q.401 0 .682.281t.281.682v4.896h4.923q.401 0 .682.281t.281.682v0q0 .375-.281.669t-.682.294z" />
      </SvgIcon>
    );
  };

  return (
    <>
      {assetsByAreaHierarchyData?.length === 0 ? <CircularProgress color="primary" /> : ''}
      {filteredSurface?.children?.length > 0 && (
        <TreeView
          id="surface"
          className={classes.root}
          defaultCollapseIcon={<MinusSquare />}
          defaultExpandIcon={<PlusSquare />}
          defaultEndIcon={<LastNodeSquare />}
          defaultExpanded={
            isShaft || isElectrical
              ? (findParent(assetsByAreaHierarchyData, assetButton, 0) as string[])
              : (findParent(assetsByAreaHierarchyData, assetName, 0) as string[])
          }
          selected={
            isElectrical ? `${assetButton}` : isShaft ? `${assetButton}${subAreaLevel}` : assetName
          }
        >
          {renderTree(filteredSurface, 0)}
        </TreeView>
      )}
      {filteredUnderground?.children?.length > 0 && (
        <TreeView
          id="underground"
          className={classes.root}
          defaultCollapseIcon={<MinusSquare />}
          defaultExpandIcon={<PlusSquare />}
          defaultEndIcon={<LastNodeSquare />}
          defaultExpanded={
            isShaft || isElectrical
              ? (findParent(assetsByAreaHierarchyData, assetButton, 0) as string[])
              : (findParent(assetsByAreaHierarchyData, assetName, 0) as string[])
          }
          selected={
            isElectrical ? `${assetButton}` : isShaft ? `${assetButton}${subAreaLevel}` : assetName
          }
        >
          {renderTree(filteredUnderground, 0)}
        </TreeView>
      )}
      {isElectrical && filteredShaft?.children?.length > 0 && (
        <TreeView
          id="shaft"
          className={classes.root}
          defaultCollapseIcon={<MinusSquare />}
          defaultExpandIcon={<PlusSquare />}
          defaultEndIcon={<LastNodeSquare />}
          defaultExpanded={findParent(assetsByAreaHierarchyData, assetButton, 0) as string[]}
          selected={assetButton}
        >
          {renderTree(filteredShaft, 0)}
        </TreeView>
      )}
    </>
  );
};

export default observer(RenderTreeView);
